import React, { useEffect, useState } from "react";
import { ChevronLeft, Award, Users } from "lucide-react";
import { BonsaiTree } from "../BonsaiTree";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getSubEmail = () => {
  try { return window.localStorage.getItem("eviq.subscription_email") || ""; } catch { return ""; }
};

const BADGE_COLORS = { bronze: "#CD7F32", silver: "#C0C0C0", gold: "#FFD700" };

const useForest = (vin) => {
  const [forest, setForest] = useState(null);
  useEffect(() => {
    const email = getSubEmail();
    if (!API_BASE || (!email && !vin)) { setForest({ exists: false }); return; }
    let cancelled = false;
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    if (vin) params.set("vin", vin);
    fetch(`${API_BASE}/api/forest/me?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setForest(d || { exists: false }); })
      .catch(() => { if (!cancelled) setForest({ exists: false }); });
    return () => { cancelled = true; };
  }, [vin]);
  return forest;
};

const CommunityBar = ({ community }) => {
  if (!community) return null;
  return (
    <div className="mt-3" data-testid="my-forest-community">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-500">
          <Users className="w-3 h-3" /> Community forest
        </div>
        <div className="font-mono text-[9px] text-zinc-500">
          {community.trees_pledged > 0 && (
            <span style={{ color: "#39FF14" }}>{community.trees_pledged.toLocaleString()} trees · </span>
          )}
          {community.sessions.toLocaleString()} / {community.milestone.toLocaleString()} sessions
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.max(1, community.progress_pct)}%`, background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
        />
      </div>
      <div className="font-mono text-[9px] text-zinc-600 mt-1">
        Every 100,000 community charging sessions plants 10,000 trees
      </div>
    </div>
  );
};

/** "My Forest" — the subscription-tied bonsai pledge ledger. */
export const MyForest = ({ vin }) => {
  const forest = useForest(vin);
  if (!forest) return null;

  if (!forest.exists) {
    return (
      <div
        className="rounded-xl border p-3 mt-3"
        style={{ borderColor: "rgba(57,255,20,0.18)", background: "rgba(0,0,0,0.24)" }}
        data-testid="my-forest-empty"
      >
        <div className="flex items-center gap-2 mb-1">
          <BonsaiTree size={18} />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#39FF14" }}>My Forest</span>
        </div>
        <div className="text-[12px] text-zinc-400 leading-snug">
          Connect your EV and start a subscription to plant your first tree — 1 tree on connection, 5 every subscription year, bonus trees for healthy packs and referrals.
        </div>
        <CommunityBar community={forest.community} />
      </div>
    );
  }

  const total = forest.trees_total || 0;
  const shown = Math.min(total, 10);
  const badge = forest.badge || {};

  return (
    <div
      className="rounded-xl border p-3 mt-3"
      style={{ borderColor: "rgba(57,255,20,0.22)", background: "rgba(0,0,0,0.28)" }}
      data-testid="my-forest-card"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BonsaiTree size={18} />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#39FF14" }}>My Forest</span>
        </div>
        {badge.level && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider border"
            style={{ color: BADGE_COLORS[badge.level], borderColor: `${BADGE_COLORS[badge.level]}55`, background: `${BADGE_COLORS[badge.level]}14` }}
            data-testid="my-forest-badge"
          >
            <Award className="w-2.5 h-2.5" /> {badge.level} grove
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold tabular-nums" style={{ color: "#39FF14" }} data-testid="my-forest-total">
          {total}
        </span>
        <span className="font-mono text-[10px] text-zinc-500">tree{total === 1 ? "" : "s"} planted in your name</span>
      </div>
      <div className="flex flex-wrap gap-0.5 mt-1.5" aria-hidden="true">
        {Array.from({ length: shown }).map((_, i) => (
          <BonsaiTree key={i} size={16} style={{ opacity: 0.55 + 0.45 * ((i + 1) / shown) }} />
        ))}
        {total > shown && <span className="font-mono text-[10px] text-zinc-500 self-end">+{total - shown}</span>}
      </div>
      <div className="font-mono text-[9px] text-zinc-500 mt-1">
        ≈ {forest.co2_kg_per_year} kg CO₂ absorbed every year
      </div>

      <div className="mt-3 divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        {(forest.entries || []).map((e) => (
          <div key={e.key} className="flex items-center gap-2 py-2" data-testid={`my-forest-entry-${e.key}`}>
            <BonsaiTree size={14} color={e.sponsored ? "#00E5FF" : "#39FF14"} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-white leading-tight">{e.title}</div>
              <div className="text-[10px] text-zinc-500 leading-tight">{e.detail}</div>
            </div>
            <div className="font-mono text-[11px] font-bold shrink-0" style={{ color: e.sponsored ? "#00E5FF" : "#39FF14" }}>
              +{e.trees}
            </div>
          </div>
        ))}
      </div>

      {forest.next_anniversary && (
        <div className="font-mono text-[9px] text-zinc-500 mt-1" data-testid="my-forest-next">
          Next anniversary: +5 trees in {forest.next_anniversary.days_away} days
        </div>
      )}
      {badge.next && (
        <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
          {badge.next.remaining} more tree{badge.next.remaining === 1 ? "" : "s"} to {badge.next.level} grove
        </div>
      )}

      <CommunityBar community={forest.community} />
    </div>
  );
};

/** Full-page sub-view for the More tab. */
export const ForestSubView = ({ back }) => (
  <div className="pt-4" data-testid="mobile-app-view-forest">
    <button
      onClick={back}
      className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-zinc-400 mb-2 px-1"
      data-testid="forest-back-btn"
    >
      <ChevronLeft className="w-4 h-4" /> Back
    </button>
    <div className="px-1 mb-1">
      <div className="text-white text-lg font-semibold">My Forest</div>
      <div className="text-zinc-500 text-xs">
        Trees pledged in your name as you subscribe, refer friends, and keep your pack healthy.
      </div>
    </div>
    <MyForest />
  </div>
);

/** Compact social-proof strip for the public Battery Passport. */
export const PassportForest = ({ vin }) => {
  const [forest, setForest] = useState(null);
  useEffect(() => {
    if (!API_BASE || !vin) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/forest/passport/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setForest(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vin]);

  if (!forest?.exists || !forest.trees_total) return null;
  const shown = Math.min(forest.trees_total, 8);
  return (
    <div
      className="rounded-xl border px-4 py-3 mb-6 flex items-center gap-3"
      style={{ borderColor: "rgba(57,255,20,0.22)", background: "rgba(57,255,20,0.05)" }}
      data-testid="passport-forest"
    >
      <div className="flex gap-0.5 shrink-0" aria-hidden="true">
        {Array.from({ length: shown }).map((_, i) => (
          <BonsaiTree key={i} size={16} style={{ opacity: 0.55 + 0.45 * ((i + 1) / shown) }} />
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-white leading-tight">
          <span className="font-semibold" style={{ color: "#39FF14" }} data-testid="passport-forest-total">
            {forest.trees_total} tree{forest.trees_total === 1 ? "" : "s"}
          </span>{" "}
          planted through this battery
        </div>
        <div className="font-mono text-[9px] text-zinc-500 mt-0.5">
          EVIQ Earth Initiative · pledged through owner milestones
        </div>
      </div>
    </div>
  );
};
